<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            id="back-to-usuarios-btn"
            color="primary"
            x-small
            text
            @click="() => $router.push({ name: 'usuarios' })"
            :disabled="submittingForm"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Usuário
        </h1>
      </v-col>
    </v-row>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        id="usuario-form"
        @submit.prevent="handleSubmit(submitForm)"
        autocomplete="off"
        :disabled="submittingForm"
      >
        <v-row>
          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="Nome Completo" rules="required" v-slot="{ errors }">
              <e-label>Nome Completo</e-label>
              <v-text-field
                id="nome-completo"
                :error-messages="errors"
                v-model="form.name"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>
          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="Login de acesso" rules="required|email" v-slot="{ errors }">
              <e-label>Login de acesso (Email)</e-label>
              <v-text-field
                id="login-acesso"
                type="email"
                :error-messages="errors"
                v-model="form.email"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>
          <v-col class="pt-0 pb-0" cols="6" v-if="!editing">
            <ValidationProvider
              name="Senha"
              :rules="editing ? 'min:4|confirmed:password' : 'required|min:4'"
              v-slot="{ errors }"
              vid="password"
            >
              <e-label>Senha</e-label>
              <v-text-field
                id="senha"
                :error-messages="errors"
                type="password"
                autocomplete="off"
                v-model="form.password"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>
          <v-col class="pt-0 pb-0" cols="6" v-if="!editing">
            <ValidationProvider
              name="Confirme a senha"
              :rules="editing ? 'min:4|confirmed:password' : 'required|min:4|confirmed:password'"
              v-slot="{ errors }"
            >
              <e-label>Confirme a senha</e-label>
              <v-text-field
                id="confirme-senha"
                :error-messages="errors"
                type="password"
                v-model="form.password_confirm"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>

          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="CPF" v-slot="{ errors }">
              <e-label>CPF (Opcional)</e-label>
              <v-text-field
                id="cpf-usuario"
                :error-messages="[...errors, cpfErroAlgoritmo].filter(Boolean)"
                v-mask="'###.###.###-##'"
                v-model="form.cpf"
                dense
                solo
                @blur="verificarCpfExistente"
                @input="validarCpfAlgoritmo"
              />
            </ValidationProvider>
          </v-col>

          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
              <e-label>Tipo</e-label>
              <e-autocomplete
                id="tipo-usuario"
                :items="outrosRolesList"
                :return-object="false"
                :item-value="(value) => value.id"
                :item-text="(value) => value.name"
                v-model="form.roles[0].id"
                @change="(value) => setRole(value)"
                label="Selecione uma opção"
                dense
                solo
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider
              name="Franquia(s) do usuário"
              :rules="franquiaObrigatoria ? 'required' : ''"
              v-slot="{ errors }"
            >
              <e-label>Franquia(s) do usuário:{{ franquiaObrigatoria ? " *" : "" }}</e-label>
              <e-autocomplete
                id="franquias-usuario"
                :items="franquiasList"
                :return-object="false"
                v-model="form.franquias_permitidas"
                :item-value="(franquia) => franquia.id"
                :item-text="(franquia) => franquia.descricao"
                :loading="carregandoFranquias"
                label="Selecione uma opção"
                dense
                multiple
                solo
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="12">
            <v-btn
              id="submit-usuario-btn"
              color="primary"
              :disabled="submittingForm"
              :loading="submittingForm"
              type="submit"
              block
              >Enviar Dados</v-btn
            >
          </v-col>
        </v-row>
      </form>

      <CpfDuplicadoModal
        :visible="mostrarModalCpfDuplicado"
        :tipo="cpfTipoEncontrado"
        :nome="cpfDadosEncontrados?.nome"
        contexto="usuario"
        @close="fecharModalCpf"
      />
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";
import CpfDuplicadoModal from "../../components/CpfDuplicadoModal.vue";
import rolesService from "../../Services/RolesService";
import useCpfValidation from "../../composables/useCpfValidation";

export default {
  components: { ELabel, CpfDuplicadoModal },
  setup() {
    const { validateCpfAlgorithm, getCpfErrorMessage } = useCpfValidation();
    return { validateCpfAlgorithm, getCpfErrorMessage };
  },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // ...mapGetters("Usuarios", ["usuarios", "usuariosList", "carregandoUsuarios"]),
    // ...mapGetters("Roles", ["outrosRolesList", "rolesList"]),
    ...mapGetters("Franquias", ["franquiasList", "carregandoFranquias"]),
    franquiaObrigatoria() {
      // Roles que exigem franquia obrigatória: secretario, coordenador, direção, aux-admin, auxiliar-servicos-gerais
      const rolesComFranquiaObrigatoria = [4, 6, 8, 10, 19];
      const roleId = this.form.roles && this.form.roles[0] && this.form.roles[0].id;
      return rolesComFranquiaObrigatoria.includes(roleId);
    },
  },
  mounted() {
    this.loadData();
  },
  data() {
    return {
      submittingForm: false,
      form: {
        roles: [{ id: null }],
        franquias_permitidas: [],
      },
      edit: false,
      franquias_permitidas: [],
      roles: [{}],
      outrosRolesList: [],
      rolesList: [],
      usuarios: [],
      carregandoUsuarios: false,
      usuariosLoaded: false,
      usuariosList: [],
      outrosUsuariosList: [],
      cpfJaExiste: false,
      cpfErroAlgoritmo: null,
      cpfTipoEncontrado: null,
      cpfDadosEncontrados: null,
      mostrarModalCpfDuplicado: false,
    };
  },
  watch: {
    form: {
      async handler(formValue) {
        try {
          const cepValue = this.$services.cepService.getCepInteiro(formValue.cep);
          if (cepValue) {
            const localizacao = await this.$services.cepService.buscarDadosDoCep(cepValue);
            this.form.municipio_residencia = localizacao.cidade;
            this.form.uf_residencia = localizacao.estado;
          }
        } catch (error) {
          this.$handleError("Erro ao buscar informações do CEP");
        }
      },
      deep: true,
    },
  },
  methods: {
    // ...mapActions("Usuarios", ["loadUsuarios"]),
    // ...mapActions("Roles", ["loadRoles"]),
    ...mapActions("Franquias", ["loadFranquias"]),

    setRole(item) {
      this.form.roles[0] = { id: item };
    },

    validarCpfAlgoritmo() {
      this.cpfErroAlgoritmo = this.getCpfErrorMessage(this.form.cpf);
    },

    async verificarCpfExistente() {
      const cpfLimpo = (this.form.cpf || "").replace(/\D/g, "");

      if (!cpfLimpo || cpfLimpo.length !== 11) {
        this.cpfJaExiste = false;
        return;
      }

      if (!this.validateCpfAlgorithm(this.form.cpf)) {
        this.cpfJaExiste = false;
        return;
      }

      try {
        const result = await this.$services.usuariosService.verificarCpfGlobal(cpfLimpo);

        if (!result.existe) {
          this.cpfJaExiste = false;
          this.cpfTipoEncontrado = null;
          this.cpfDadosEncontrados = null;
          return;
        }

        // Se está editando e encontrou o mesmo usuário, não mostrar erro
        if (this.editing && result.tipo === "usuario" && result.dados.id === this.form.id) {
          this.cpfJaExiste = false;
          this.cpfTipoEncontrado = null;
          this.cpfDadosEncontrados = null;
          return;
        }

        this.cpfJaExiste = true;
        this.cpfTipoEncontrado = result.tipo;
        this.cpfDadosEncontrados = result.dados;
        this.mostrarModalCpfDuplicado = true;
      } catch (error) {
        this.$toast.danger("Erro ao verificar CPF.");
        this.cpfJaExiste = false;
      }
    },

    fecharModalCpf() {
      this.mostrarModalCpfDuplicado = false;
      this.form.cpf = "";
    },

    async carregarRoles() {
      this.outrosRolesList = await rolesService.syncAll();
    },

    async pegarUsuario() {
      try {
        const { usuario_id } = this.$route.params;
        const response = await this.$services.usuariosService.getUsuario(usuario_id);
        return response;
      } catch (error) {
        return this.$handleError(error);
      }
    },

    async submitForm() {
      if (!this.editing && this.form.password !== this.form.password_confirm) {
        this.$toast.warn("As senhas não estão iguais");
        return;
      }
      const rolesComFranquiaObrigatoria = [4, 6, 8, 10, 19];
      const roleId = this.form.roles && this.form.roles[0] && this.form.roles[0].id;
      if (rolesComFranquiaObrigatoria.includes(roleId)) {
        if (!this.form.franquias_permitidas || this.form.franquias_permitidas.length === 0) {
          this.$toast.warn("Selecione pelo menos uma franquia para este tipo de usuário");
          return;
        }
      }
      this.form.especial = Boolean(this.form.especial);
      this.submittingForm = true;
      if (!this.form.password) delete this.form.password;
      try {
        if (this.editing) {
          await this.$services.usuariosService.atualizarUsuario(this.form);
          this.$toast.success("Usuario(a) Atualizado(a) com sucesso");
        } else {
          await this.$services.usuariosService.criarUsuario(this.form);
          this.$toast.success("Usuario(a) criado(a) com sucesso");
        }
        this.$router.push({ name: "usuarios" });
      } catch (error) {
        this.$handleError(error);
      }
      this.submittingForm = false;
    },

    async loadData() {
      try {
        if (
          this.usuarios.length === 0 ||
          this.rolesList.length === 0 ||
          this.franquiasList.length === 0
        ) {
          this.$loaderService.open("Carregando dados");
        }
        await Promise.all([this.carregarRoles(), this.loadFranquias()]);
        this.$loaderService.close();
        this.$forceUpdate();
        this.loadFormData();
      } catch (error) {
        this.$handleError(error);
      }
    },

    async loadFormData() {
      if (!this.usuarios || !this.editing) {
        return;
      }

      // const { usuario_id } = this.$route.params;
      // const usuario = this.usuarios.get(parseInt(usuario_id, 10));

      const usuario = await this.pegarUsuario();
      if (!usuario || this.form.id) return;
      this.form = {
        ...usuario,
      };
    },
  },
};
</script>

<style></style>
